import React, { useState, useMemo } from 'react';
import { DollarSign, Calendar, Percent, Wallet } from 'lucide-react';
import CalculatorInput from '../shared/CalculatorInput';
import ResultCard from '../shared/ResultCard';
import Chart from '../shared/Chart';

const RothIRACalculator: React.FC = () => {
  const [currentAge, setCurrentAge] = useState(30);
  const [retirementAge, setRetirementAge] = useState(65);
  const [currentBalance, setCurrentBalance] = useState(6000);
  const [annualContribution, setAnnualContribution] = useState(6000);
  const [annualReturn, setAnnualReturn] = useState(7);

  const calculatedData = useMemo(() => {
    const years = retirementAge - currentAge;
    const monthlyRate = annualReturn / 100 / 12;
    const monthlyContribution = annualContribution / 12;
    const months = years * 12;
    const data = [];

    let balance = currentBalance;
    let totalContributed = currentBalance;

    for (let i = 0; i <= months; i++) {
      if (i > 0) {
        balance = balance * (1 + monthlyRate) + monthlyContribution;
        totalContributed += monthlyContribution;
      }

      if (i % 12 === 0) {
        data.push({
          year: currentAge + i / 12,
          balance: Math.round(balance),
          contributed: Math.round(totalContributed),
        });
      }
    }

    return data;
  }, [currentAge, retirementAge, currentBalance, annualContribution, annualReturn]);

  const finalBalance = calculatedData[calculatedData.length - 1]?.balance || 0;
  const totalContributed = calculatedData[calculatedData.length - 1]?.contributed || 0;
  const totalEarnings = finalBalance - totalContributed;

  return (
    <div className="bg-white rounded-2xl shadow-xl overflow-hidden p-6 sm:p-8">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Roth IRA Calculator</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        <CalculatorInput
          label="Current Age"
          value={currentAge}
          onChange={setCurrentAge}
          icon={<Calendar size={20} />}
          suffix="years"
        />
        <CalculatorInput
          label="Retirement Age"
          value={retirementAge}
          onChange={setRetirementAge}
          icon={<Calendar size={20} />}
          suffix="years"
        />
        <CalculatorInput
          label="Current Balance"
          value={currentBalance}
          onChange={setCurrentBalance}
          icon={<Wallet size={20} />}
          prefix="$"
        />
        <CalculatorInput
          label="Annual Contribution"
          value={annualContribution}
          onChange={setAnnualContribution}
          icon={<DollarSign size={20} />}
          prefix="$"
        />
        <CalculatorInput
          label="Expected Annual Return"
          value={annualReturn}
          onChange={setAnnualReturn}
          icon={<Percent size={20} />}
          suffix="%"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <ResultCard
          title="Total Balance"
          value={finalBalance}
          description="Projected balance at retirement"
          trend="up"
        />
        <ResultCard
          title="Total Contributed"
          value={totalContributed}
          description="Your total contributions"
          trend="up"
        />
        <ResultCard
          title="Tax-Free Earnings"
          value={totalEarnings}
          description="Total tax-free growth"
          trend="up"
        />
      </div>

      <Chart data={calculatedData} />
    </div>
  );
};

export default RothIRACalculator;